import React, { useState } from 'react';

const InstanceCard = ({ instance, onAction }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const getStatusColor = (status) => {
    const colors = {
      'running': 'bg-green-500',
      'stopped': 'bg-gray-500',
      'creating': 'bg-yellow-500 animate-pulse',
      'error': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-400';
  };

  const getOSIcon = (osImage) => {
    const icons = {
      'ubuntu': '🐧',
      'debian': '🌀',
      'centos': '🎯',
      'fedora': '🎩',
      'rocky': '🗻'
    };
    return icons[osImage?.split('-')[0]] || '🖥️';
  };

  return (
    <div className="bg-slate-800 rounded-lg p-6 hover:bg-slate-750 transition-all hover:shadow-xl relative group">
      {/* Status Indicator */}
      <div className="absolute top-4 right-4">
        <div className={`w-3 h-3 rounded-full ${getStatusColor(instance.status)}`}></div>
      </div>

      {/* Icon & Name */}
      <div className="flex items-start space-x-4 mb-4">
        <div className="text-5xl">{getOSIcon(instance.os_image)}</div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-1">{instance.name}</h3>
          <p className="text-sm text-gray-400 capitalize">{instance.status}</p>
        </div>
      </div>

      {/* Specs */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">vCPU</span>
          <span className="text-white font-medium">{instance.vcpus}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">RAM</span>
          <span className="text-white font-medium">{instance.ram_gb} GB</span>
        </div>
        {instance.ssh_port && (
          <div className="mt-2 bg-slate-900 rounded p-2">
            <span className="text-gray-400 text-xs block mb-1">SSH</span>
            <span className="text-green-400 font-mono text-xs">ssh -p {instance.ssh_port} ubuntu@ssh.aggregatededgenetwork.com</span>
          </div>
        )}

        {instance.status === 'running' && instance.id && (
          <div className="mt-2 bg-slate-900 rounded p-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-400 text-xs">Hostname</span>
              <button
                onClick={() => navigator.clipboard.writeText(`${instance.id.substring(0,8)}.nodes.aggregatededgenetwork.com`)}
                className="text-gray-500 hover:text-gray-300 text-xs"
                title="Copy hostname"
              >
                Copy
              </button>
            </div>
            <span className="text-blue-400 font-mono text-xs">{instance.id.substring(0,8)}.nodes.aggregatededgenetwork.com</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex space-x-2">
        <button
          onClick={() => onAction('console', instance.id)}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 rounded-md transition-colors"
        >
          Console
        </button>

        <button
          onClick={() => onAction('metrics', instance.id)}
          className="flex-1 bg-slate-700 hover:bg-slate-600 text-white text-sm py-2 rounded-md transition-colors"
          title="View metrics"
        >
          📊 Metrics
        </button>
        
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 bg-slate-700 hover:bg-slate-600 rounded-md transition-colors"
          >
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-slate-700 rounded-md shadow-lg z-10 border border-slate-600">
              {instance.status === 'running' && (
                <button
                  onClick={() => { onAction('stop', instance.id); setMenuOpen(false); }}
                  className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-600"
                >
                  ⏸️ Stop Instance
                </button>
              )}
              {instance.status === 'stopped' && (
                <button
                  onClick={() => { onAction('start', instance.id); setMenuOpen(false); }}
                  className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-600"
                >
                  ▶️ Start Instance
                </button>
              )}
              <button
                onClick={() => { onAction('snapshot', instance.id); setMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-600"
              >
                📸 Snapshot
              </button>
              <button
                onClick={() => { onAction('domain', instance.id); setMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-600"
              >
                🌐 Connect Domain
              </button>
              <button
                onClick={() => { onAction('resize', instance.id); setMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-slate-600"
              >
                📏 Resize
              </button>
              <hr className="border-slate-600 my-1" />
              <button
                onClick={() => { onAction('delete', instance.id); setMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-slate-600"
              >
                🗑️ Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hover Stats */}
      <div className="mt-4 pt-4 border-t border-slate-700 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="text-center">
            <div className="text-gray-400">CPU</div>
            <div className="text-white font-medium">12%</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400">RAM</div>
            <div className="text-white font-medium">45%</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400">Disk</div>
            <div className="text-white font-medium">23%</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstanceCard;
